import { useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Badge, ButtonPrimitive, Switch, HiddenLabel, Text } from '@figma/fpl-components';
import fplTokens from '@figma/fpl-tokens/css-vars';
import { color as fplColor, spacer } from '@figma/fpl-tokens/tokens.stylex';
import * as stylex from '@stylexjs/stylex';
import {
  FF_ANNUAL_DISCOUNT_30,
  FF_WEAVE_PLAN_OVERVIEW_ENTERPRISE_CARD,
  FF_WEAVE_MAX_TIER,
  FF_WEAVE_MAX_TIER_FRONTEND,
} from '@/consts/featureFlags';
import { getAnnualDiscountPercent, getPricingDetails, getPricingPlanDetails } from '@/consts/pricing.consts';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { Box } from '@/UI/Box/Box';
import { TextLgRg, Typography } from '@/UI/AppTypography/AppTypography';
import { Grid } from '@/UI/Grid/Grid';
import { AppIconButton } from '@/UI/Buttons/AppIconButton';
import { I18N_KEYS } from '@/language/keys';
import { SubscriptionType, BillingCycle } from '@/types/shared';
import {
  PricingPackage,
  isDiscountEligiblePlan,
} from '@/components/SubscriptionsAndPayments/PricingPage/PricingPackage';
import EnterprisePackage from '@/components/SubscriptionsAndPayments/PricingPage/EnterprisePackage';
import {
  maxTierAccentStyles,
  maxTierBadgeStyles,
} from '@/components/SubscriptionsAndPayments/PricingPage/pricing.styles';
import CreditsTable from '@/components/SubscriptionsAndPayments/PricingPage/CreditsTable';
import PricingFAQS from '@/components/SubscriptionsAndPayments/PricingPage/PricingFAQS';
import { AuthContext } from '@/contexts/AuthContext';
import { PricingPageContainer, PricingPageEntrypoint } from '@/enums/pricing-page.enum';
import { WEAVY_PAYWALL_ENTRYPOINTS } from '@/consts/weavyPaywallEntrypoints.consts';
import { useAnalytics, TrackTypeEnum } from '@/hooks/useAnalytics';
import { useBanner, APP_BANNER_ID } from '@/hooks/useBanner';
import { FlexColCenHor, FlexCenHor, FlexColCenHorVer } from '@/UI/styles';
import { WeavyLogoFilled } from '@/UI/Icons/WeavyLogoFilled';
import { useIsSubscriptionDiscountActive } from '@/hooks/useIsSubscriptionDiscountActive';
import { hasKnownPersonalEmailDomain } from '@/utils/email-domain.utils';
import type { TFunction } from 'i18next';
import type { PricingFeature } from '@/components/SubscriptionsAndPayments/PricingPage/PricingPackage';
import type { PricingPageViewAnalyticsContext } from '@/enums/pricing-page.enum';

const fplTypographyStyles = stylex.create({
  title: {
    margin: 0,
  },
  /** The Max-tier layout's title spans the content width, up to `maxWidth`. */
  maxTierTitle: (maxWidth: string) => ({
    width: '100%',
    maxWidth,
  }),
  videoSubtitle: {
    margin: 0,
    marginTop: spacer.spacer4,
    textAlign: 'center',
  },
});

const annualDiscountBannerStyles = stylex.create({
  banner: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    minHeight: '62px',
    padding: `${spacer.spacer3} ${spacer.spacer4}`,
    color: 'var(--weave-pricing-promotion-text)',
    backgroundColor: 'rgba(247, 255, 168, 0.16)',
    textAlign: 'center',
  },
  bannerText: {
    fontSize: '15px',
    lineHeight: '25px',
    letterSpacing: '-0.075px',
  },
});

const fplBillingToggleStyles = stylex.create({
  group: {
    display: 'inline-flex',
    width: 'fit-content',
    height: '46px',
    gap: spacer.spacer1,
    padding: spacer.spacer1,
    position: 'relative',
    borderRadius: '8px',
    backgroundColor: fplColor.bgElevated,
    '::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      border: `1px solid ${fplColor.border}`,
      borderRadius: '8px',
      pointerEvents: 'none',
    },
  },
  button: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: '0 0 auto',
    minWidth: '140px',
    height: '38px',
    gap: spacer.spacer12px,
    borderRadius: '6px',
    padding: `${spacer.spacer2} ${spacer.spacer3}`,
    color: fplColor.text,
    backgroundColor: 'transparent',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    outline: {
      default: 'none',
      ':focus-visible': `1px solid ${fplColor.borderSelected}`,
    },
    outlineOffset: {
      default: 0,
      ':focus-visible': '1px',
    },
  },
  buttonSelected: {
    color: fplColor.text,
    backgroundColor: fplColor.bgHover,
  },
  discountBadgeNoWrap: {
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  inactiveDiscountBadge: {
    '--color-text': fplColor.textSecondary,
  },
  annualDiscountBadge: {
    '--fpl-badge-color': 'var(--weave-pricing-badge-text)',
    '--fpl-badge-border-color': 'var(--ramp-lime-400)',
  },
});

const createPricingFeature = (text: string, isUnavailable?: boolean): PricingFeature => ({ text, isUnavailable });

/** Selects shared plan packages and includes Max only when both rollout gates allow it. */
const getPricingPackages = (
  t: TFunction,
  showFreeCard: boolean,
  isAnnualDiscount30Enabled = false,
  hasMaxCard = false,
) => {
  const keys = I18N_KEYS.PAYMENTS.PRICING_TABLE;
  const options = { isAnnualDiscount30Enabled, isMaxTierLayout: hasMaxCard };
  const packages = Object.values(getPricingDetails(t, options));
  if (hasMaxCard) packages.splice(3, 0, getPricingPlanDetails(t, SubscriptionType.Max, options));
  return packages.map((basePackage) => {
    const pkg = { ...basePackage, name: basePackage.planType };
    if (!showFreeCard && pkg.name === SubscriptionType.Starter) {
      return {
        ...pkg,
        featuresTitle: '',
        features: [
          createPricingFeature(t(keys.FEATURE.ALL_MODELS)),
          createPricingFeature(t(keys.FEATURE.PROFESSIONAL_GRADE_EDITING_TOOLS)),
          createPricingFeature(t(keys.FEATURE.WORKFLOW_COLLABORATION)),
          createPricingFeature(t(keys.SUBTITLE.INCLUDED_FEATURE_03)),
          createPricingFeature(t(keys.FEATURE.UNLIMITED_WORKFLOWS)),
          createPricingFeature(t(keys.FEATURE.CREDIT_TOP_UP, { price: 10, credits: 1000 })),
        ],
      };
    }

    return pkg;
  });
};

interface PricingTablePageProps {
  container: PricingPageContainer;
  analyticsContext?: PricingPageViewAnalyticsContext;
}
const ENTERPRISE_CARD_ANALYTICS_PROPERTY = 'weave_enterprise_card_variant';

const DISCOUNT_GO_LIVE_DATE = 'Sep 10th, 2026';

export const PricingTablePage = (props: PricingTablePageProps) => {
  const { currentUser } = useContext(AuthContext);
  const { value: isEnterprisePlanCardFlagEnabled } = useFeatureFlag(FF_WEAVE_PLAN_OVERVIEW_ENTERPRISE_CARD);
  const { value: isMaxTierEnabled } = useFeatureFlag(FF_WEAVE_MAX_TIER);
  const { value: isMaxFrontendEnabled } = useFeatureFlag(FF_WEAVE_MAX_TIER_FRONTEND);
  const workspaceId = currentUser?.activeWorkspace?.workspaceId;
  const email = currentUser?.email;
  const hasNonPersonalEmail = Boolean(email && !hasKnownPersonalEmailDomain(email));

  return (
    <PricingTablePageContent
      {...props}
      hasMaxCard={Boolean(workspaceId) && isMaxFrontendEnabled === true && isMaxTierEnabled === true}
      isEnterprisePlanCardExperiment={hasNonPersonalEmail && isEnterprisePlanCardFlagEnabled === true}
    />
  );
};

interface PricingTablePageContentProps extends PricingTablePageProps {
  hasMaxCard: boolean;
  isEnterprisePlanCardExperiment: boolean;
}

const PricingTablePageContent = ({
  container,
  analyticsContext,
  hasMaxCard,
  isEnterprisePlanCardExperiment: isEnterprisePlanCardExperimentEligible,
}: PricingTablePageContentProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentUser } = useContext(AuthContext);
  const videoCopy =
    analyticsContext?.entrypoint === PricingPageEntrypoint.VideoModelPaidPlan ||
    analyticsContext?.entrypoint === PricingPageEntrypoint.VideoModelPaywall
      ? WEAVY_PAYWALL_ENTRYPOINTS.VIDEO_MODEL.copy
      : undefined;
  const { track } = useAnalytics();
  // Keep the current-plan card in sync when the subscription webhook updates AuthContext.
  const currentUserPlan = currentUser?.activeWorkspace?.subscription?.type || SubscriptionType.Free;
  const currentUserBillingCycle = currentUser?.activeWorkspace?.subscription?.billingCycle || BillingCycle.Yearly;
  const { isVisible: isBannerVisible } = useBanner(APP_BANNER_ID);
  const isSubscriptionDiscountActive = useIsSubscriptionDiscountActive();
  const { value: annualDiscount30Flag } = useFeatureFlag(FF_ANNUAL_DISCOUNT_30);
  const isAnnualDiscount30Enabled = annualDiscount30Flag === true;
  const annualDiscountPercent = getAnnualDiscountPercent(isAnnualDiscount30Enabled);
  const [billingCycle, setBillingCycle] = useState(BillingCycle.Yearly);
  const { offset: bannerOffset } = useBanner(APP_BANNER_ID);
  const [audience, setAudience] = useState<'individuals' | 'teams'>('individuals');
  const isEnterprisePlanCardExperiment = isEnterprisePlanCardExperimentEligible && !hasMaxCard;
  const MAX_WIDTH = hasMaxCard ? '1328px' : currentUserPlan !== SubscriptionType.Free ? '936px' : '1248px';
  const experimentAnalytics = {
    ...(isEnterprisePlanCardExperiment ? { [ENTERPRISE_CARD_ANALYTICS_PROPERTY]: 'test' } : {}),
  };
  const annualDiscountLabel = t(
    isAnnualDiscount30Enabled
      ? I18N_KEYS.PAYMENTS.PRICING_TABLE.TOGGLE_BUTTON.YEARLY_SAVE_DISCOUNT
      : hasMaxCard
        ? I18N_KEYS.PAYMENTS.PRICING_TABLE.TOGGLE_BUTTON.YEARLY_UP_TO_DISCOUNT
        : I18N_KEYS.PAYMENTS.PRICING_TABLE.TOGGLE_BUTTON.YEARLY_FIXED_DISCOUNT,
    { discountPercent: annualDiscountPercent },
  );
  const annualLabel = t(I18N_KEYS.PAYMENTS.PRICING_TABLE.TOGGLE_BUTTON.YEARLY);
  // Adding Enterprise replaces the Free card so the grid stays at four cards.
  const showFreeCard = currentUserPlan === SubscriptionType.Free && !isEnterprisePlanCardExperiment;
  const visiblePricingPackages = getPricingPackages(t, showFreeCard, isAnnualDiscount30Enabled, hasMaxCard).filter(
    (pricingPackage) => pricingPackage.name !== SubscriptionType.Free || showFreeCard,
  );
  // The campaign is for new paid subscriptions, so it is advertised only to a workspace that does
  // not have one — a paid workspace upgrading is charged in full, and showing it the banner would
  // promise a discount no card can offer. Logged-out visitors read as Free, which is right: they
  // are the audience, and they reach checkout through sign-up.
  const isFreePlan = currentUserPlan === SubscriptionType.Free;
  const isMonthlyPaidPlan = !isFreePlan && currentUserBillingCycle === BillingCycle.Monthly;
  const isEligibleForAnnualDiscountBanner = isFreePlan || isMonthlyPaidPlan;
  const isDiscountActive = isSubscriptionDiscountActive && isFreePlan;
  const showAnnualDiscountBanner =
    !hasMaxCard && isAnnualDiscount30Enabled && !isSubscriptionDiscountActive && isEligibleForAnnualDiscountBanner;
  // Reserve the discount badge on every card only when some visible card actually shows it, so
  // alignment is preserved without leaving a blank gap when no card qualifies.
  const hasDiscountInView =
    isDiscountActive &&
    visiblePricingPackages.some((pricingPackage) => isDiscountEligiblePlan(currentUserPlan, pricingPackage.name));

  useEffect(() => {
    track(
      'Opened_pricing_table',
      {
        context: 'Payments',
        context_data: { container },
        ...(currentUser?.activeWorkspace?.workspaceId ? { workspace_id: currentUser.activeWorkspace.workspaceId } : {}),
        ...analyticsContext,
        ...experimentAnalytics,
      },
      TrackTypeEnum.BI,
    );
    // eslint-disable-next-line react-hooks-js/exhaustive-deps
  }, []);

  const handleBillingCycleChange = (newBillingCycle: BillingCycle | null) => {
    track(
      'Pricing Table - Changed_billing_cycle',
      { billingCycle: newBillingCycle, ...experimentAnalytics },
      TrackTypeEnum.BI,
    );
    if (newBillingCycle) {
      setBillingCycle(newBillingCycle);
    }
  };

  const renderPricingPackage = (pricingPackage: ReturnType<typeof getPricingPackages>[number]) => (
    <Grid
      size={{
        xs: 12,
        sm: hasMaxCard && pricingPackage.name === SubscriptionType.Team ? 12 : 6,
        md: hasMaxCard ? (pricingPackage.name === SubscriptionType.Team ? 12 : 6) : 4,
        lg: hasMaxCard
          ? pricingPackage.name === SubscriptionType.Team
            ? 6
            : showFreeCard
              ? 3
              : 4
          : isEnterprisePlanCardExperiment || currentUserPlan === SubscriptionType.Free
            ? 3
            : 4,
      }}
      key={pricingPackage.name}
      sx={{
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <PricingPackage
        subscriptionType={pricingPackage.name}
        description={pricingPackage.description}
        monthlyPrice={pricingPackage.monthlyPrice}
        annualPrice={pricingPackage.annualPrice}
        annualTotal={pricingPackage.annualTotal}
        firstInvoicePrices={pricingPackage.firstInvoicePrices}
        credits={pricingPackage.credits}
        creditsInfo={pricingPackage.creditsInfo}
        featuresTitle={pricingPackage.featuresTitle}
        features={pricingPackage.features}
        highlightedFeatureIndex={pricingPackage.highlightedFeatureIndex}
        billingCycle={billingCycle}
        currentUserPlan={currentUserPlan}
        currentUserBillingCycle={currentUserBillingCycle}
        isPopular={
          hasMaxCard ? pricingPackage.name === SubscriptionType.Max : pricingPackage.name === SubscriptionType.Pro
        }
        isMaxTierLayout={hasMaxCard}
        analyticsContext={{ ...analyticsContext, ...experimentAnalytics }}
        workspaceId={currentUser?.activeWorkspace?.workspaceId}
        downgradeData={{
          date: currentUser?.activeWorkspace?.subscription?.scheduledDowngradeDate,
          plan: currentUser?.activeWorkspace?.subscription?.scheduledDowngradePlan,
        }}
        currentUser={currentUser || undefined}
        isDiscountActive={isDiscountActive}
        reserveDiscountSpace={hasDiscountInView}
      />
    </Grid>
  );

  return (
    <>
      {showAnnualDiscountBanner && (
        <div data-testid="annual-discount-banner" {...stylex.props(annualDiscountBannerStyles.banner)}>
          <Text.span variant="body-large-strong" {...stylex.props(annualDiscountBannerStyles.bannerText)}>
            {t(I18N_KEYS.PAYMENTS.PRICING_TABLE.ANNUAL_DISCOUNT_BANNER)}
          </Text.span>
        </div>
      )}
      <FlexColCenHor
        data-testid="pricing-table-page-container"
        sx={{
          width: '100%',
          overflowY: 'auto',
          background: fplTokens.color.bgElevated,
          backdropFilter: `blur(3px)`,
          WebkitBackdropFilter: `blur(3px)`,
          px: { xs: 4, md: hasMaxCard ? '56px' : 12 },
          pb: { xs: 4, md: '142px' },
          pt: {
            xs: 4,
            md: hasMaxCard ? '48px' : showAnnualDiscountBanner ? '78px' : '100px',
          },
        }}
      >
        {/* The video headline is centered over its subtitle, also where the Max-tier title is otherwise left-aligned. */}
        <Text.h1
          variant="display"
          color="default"
          align={videoCopy ? 'center' : undefined}
          {...stylex.props(fplTypographyStyles.title, hasMaxCard && fplTypographyStyles.maxTierTitle(MAX_WIDTH))}
        >
          {videoCopy
            ? t(videoCopy.title)
            : currentUser
              ? t(I18N_KEYS.PAYMENTS.PRICING_TABLE.TITLE)
              : t(I18N_KEYS.PAYMENTS.PRICING_TABLE.TEMP_TITLE)}
        </Text.h1>
        {videoCopy && (
          <Text.p variant="body-large" {...stylex.props(fplTypographyStyles.videoSubtitle)}>
            {t(I18N_KEYS.PAYWALLS.VIDEO_MODEL_PAYWALL.FULL_PRICING_DESCRIPTION)}
          </Text.p>
        )}
        {isDiscountActive && (
          <Box
            data-testid="pricing-discount-banner"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mt: '16px',
              px: 3,
              width: '100%',
              maxWidth: '540px',
              minHeight: '40px',
              borderRadius: '5px',
              textAlign: 'center',
              backgroundColor: 'var(--weave-pricing-promotion-background)',
              color: 'var(--weave-pricing-promotion-text)',
              fontSize: '15px',
              lineHeight: '25px',
              fontWeight: 550,
            }}
          >
            {t(I18N_KEYS.PAYMENTS.PRICING_TABLE.DISCOUNT.BANNER)}
          </Box>
        )}
        {/* Toggle Monthly / Annually */}
        <Box
          sx={{
            mb: hasMaxCard ? '32px' : '40px',
            mt: '24px',
            ...(hasMaxCard
              ? {
                  width: '100%',
                  maxWidth: MAX_WIDTH,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  flexWrap: 'wrap',
                }
              : {}),
          }}
        >
          {hasMaxCard && (
            <div
              role="group"
              aria-label={t(I18N_KEYS.PAYMENTS.PRICING_TABLE.AUDIENCE)}
              {...stylex.props(fplBillingToggleStyles.group)}
            >
              {(['individuals', 'teams'] as const).map((value) => (
                <ButtonPrimitive
                  key={value}
                  aria-pressed={audience === value}
                  onClick={() => setAudience(value)}
                  {...stylex.props(
                    fplBillingToggleStyles.button,
                    audience === value && fplBillingToggleStyles.buttonSelected,
                  )}
                >
                  <Text.span variant="body-large">
                    {t(
                      value === 'individuals'
                        ? I18N_KEYS.PAYMENTS.PRICING_TABLE.INDIVIDUALS
                        : I18N_KEYS.PAYMENTS.PRICING_TABLE.TEAMS,
                    )}
                  </Text.span>
                </ButtonPrimitive>
              ))}
            </div>
          )}
          {hasMaxCard ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ButtonPrimitive
                onClick={() => handleBillingCycleChange(BillingCycle.Monthly)}
                aria-pressed={billingCycle === BillingCycle.Monthly}
              >
                <Text.span variant="body-large" color="default">
                  {t(I18N_KEYS.PAYMENTS.PRICING_TABLE.TOGGLE_BUTTON.MONTHLY)}
                </Text.span>
              </ButtonPrimitive>
              <Box sx={maxTierAccentStyles}>
                <Switch
                  label={<HiddenLabel>{t(I18N_KEYS.PAYMENTS.PRICING_TABLE.TOGGLE_BUTTON.ARIA)}</HiddenLabel>}
                  checked={billingCycle === BillingCycle.Yearly}
                  onChange={(checked) => handleBillingCycleChange(checked ? BillingCycle.Yearly : BillingCycle.Monthly)}
                />
              </Box>
              <ButtonPrimitive
                onClick={() => handleBillingCycleChange(BillingCycle.Yearly)}
                aria-pressed={billingCycle === BillingCycle.Yearly}
              >
                <Text.span variant="body-large" color="default">
                  {annualLabel}
                </Text.span>
              </ButtonPrimitive>
              <Box
                sx={billingCycle === BillingCycle.Yearly ? maxTierBadgeStyles : undefined}
                {...stylex.props(billingCycle === BillingCycle.Monthly && fplBillingToggleStyles.inactiveDiscountBadge)}
              >
                <Badge
                  data-testid="billing-discount-badge"
                  variant={billingCycle === BillingCycle.Yearly ? 'brandFilled' : 'defaultFilled'}
                  size="lg"
                >
                  {annualDiscountLabel}
                </Badge>
              </Box>
            </Box>
          ) : (
            <div
              role="group"
              aria-label={t(I18N_KEYS.PAYMENTS.PRICING_TABLE.TOGGLE_BUTTON.ARIA)}
              {...stylex.props(fplBillingToggleStyles.group)}
            >
              {[BillingCycle.Monthly, BillingCycle.Yearly].map((cycle) => {
                const isSelected = billingCycle === cycle;
                const isYearly = cycle === BillingCycle.Yearly;
                return (
                  <ButtonPrimitive
                    key={cycle}
                    aria-pressed={isSelected}
                    onClick={() => handleBillingCycleChange(cycle)}
                    style={
                      isSelected && !isAnnualDiscount30Enabled
                        ? ({
                            '--fpl-badge-color': fplTokens.color.text,
                            '--fpl-badge-border-color': fplTokens.ramp.grey200,
                          } as React.CSSProperties)
                        : undefined
                    }
                    {...stylex.props(
                      fplBillingToggleStyles.button,
                      isSelected && fplBillingToggleStyles.buttonSelected,
                    )}
                  >
                    <Text.span
                      variant={!isSelected ? 'body-large' : 'body-large-strong'}
                      color={isSelected ? 'default' : 'secondary'}
                    >
                      {isYearly ? annualLabel : t(I18N_KEYS.PAYMENTS.PRICING_TABLE.TOGGLE_BUTTON.MONTHLY)}
                    </Text.span>
                    {isYearly && !isDiscountActive ? (
                      <span
                        {...stylex.props(
                          fplBillingToggleStyles.discountBadgeNoWrap,
                          isAnnualDiscount30Enabled &&
                            currentUserPlan === SubscriptionType.Free &&
                            fplBillingToggleStyles.annualDiscountBadge,
                        )}
                      >
                        <Badge
                          data-testid="billing-discount-badge"
                          variant="brandOutline"
                          size={isAnnualDiscount30Enabled ? 'lg' : 'md'}
                        >
                          {annualDiscountLabel}
                        </Badge>
                      </span>
                    ) : null}
                  </ButtonPrimitive>
                );
              })}
            </div>
          )}
        </Box>
        {/* Packages */}
        <Grid
          container
          data-testid="pricing-table-packages-container"
          spacing={2}
          sx={{
            width: hasMaxCard ? '100%' : undefined,
            maxWidth: hasMaxCard ? MAX_WIDTH : '1248px',
            justifyContent: hasMaxCard ? 'flex-start' : 'center',
            mb: isDiscountActive ? '46px' : 12,
          }}
        >
          {visiblePricingPackages
            .filter((pkg) => !hasMaxCard || (audience === 'individuals' && pkg.name !== SubscriptionType.Team))
            .map(renderPricingPackage)}
          {hasMaxCard && (
            <>
              {audience === 'individuals' && (
                <Grid size={12}>
                  <Text.h2 variant="heading-medium" color="default">
                    {t(I18N_KEYS.PAYMENTS.PRICING_TABLE.FOR_TEAMS)}
                  </Text.h2>
                </Grid>
              )}
              {visiblePricingPackages.filter((pkg) => pkg.name === SubscriptionType.Team).map(renderPricingPackage)}
              <Grid size={{ xs: 12, lg: 6 }} sx={{ display: 'flex' }}>
                <EnterprisePackage isMaxTierLayout analyticsContext={{ ...analyticsContext, ...experimentAnalytics }} />
              </Grid>
            </>
          )}
          {isEnterprisePlanCardExperiment && (
            <Grid
              size={{ xs: 12, sm: 6, md: 4, lg: 3 }}
              sx={{
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <EnterprisePackage
                isEnterprisePlanCardExperiment
                reserveDiscountSpace={hasDiscountInView}
                analyticsContext={{ ...analyticsContext, ...experimentAnalytics }}
              />
            </Grid>
          )}
        </Grid>
        {/* Campaign eligibility footnote */}
        {isDiscountActive && (
          <FlexCenHor sx={{ width: '100%', maxWidth: MAX_WIDTH, mb: 12 }}>
            <Typography
              data-testid="pricing-discount-footnote"
              sx={{
                fontSize: '14px',
                color: fplTokens.color.text,
                textAlign: 'center',
              }}
            >
              {t(I18N_KEYS.PAYMENTS.PRICING_TABLE.DISCOUNT.FOOTNOTE, { goLiveDate: DISCOUNT_GO_LIVE_DATE })}
            </Typography>
          </FlexCenHor>
        )}
        {/* Enterprise Package */}
        {!isEnterprisePlanCardExperiment && !hasMaxCard && (
          <FlexCenHor
            sx={{
              width: '100%',
              maxWidth: '992px',
              mb: 12,
            }}
          >
            <EnterprisePackage analyticsContext={{ ...analyticsContext, ...experimentAnalytics }} />
          </FlexCenHor>
        )}

        {/* Holiday Promo Disclaimer */}
        {isBannerVisible && (
          <Box sx={{ width: { xs: '90%', md: '720px' }, textAlign: 'center' }}>
            <Typography
              sx={{
                fontSize: '14px',
                color: fplColor.textSecondary,
              }}
            >
              {t(I18N_KEYS.WEAVY_MARKETING.APP_BANNER.HOLIDAY_PROMO.DISCLAIMER)}
            </Typography>
          </Box>
        )}
        {/* Partners */}

        <FlexColCenHorVer
          sx={{
            width: '100%',
            pt: 0,
            mb: 12,
          }}
        >
          <FlexCenHor sx={{ width: '100%', mb: 2 }}>
            <TextLgRg>{t(I18N_KEYS.PAYMENTS.PRICING_TABLE.PARTNERS_TITLE)}</TextLgRg>
          </FlexCenHor>
          <FlexCenHor
            sx={{
              gap: 0,
              flexWrap: 'wrap',
              width: '75%',
            }}
          >
            {Array.from({ length: 12 }).map((_, index) => {
              const src = `/partners-logos/partner_logo_0${index + 1}.svg`;
              return (
                <Box
                  key={index}
                  component="span"
                  role="img"
                  aria-label={`Logo ${index + 1}`}
                  sx={{
                    width: 150,
                    height: 100,
                    backgroundColor: 'var(--weave-pricing-partner-logo)',
                    mask: `url("${src}") center / contain no-repeat`,
                    maskMode: 'luminance',
                  }}
                />
              );
            })}
          </FlexCenHor>
        </FlexColCenHorVer>

        <FlexColCenHor
          sx={{
            width: '100%',
            pt: 0,
            mb: 12,
          }}
        >
          <FlexCenHor sx={{ width: '100%', mb: 3 }}>
            <TextLgRg>{t(I18N_KEYS.PAYMENTS.PRICING_TABLE.CREDITS_TABLE_TITLE)}</TextLgRg>
          </FlexCenHor>
          <Box sx={{ width: '100%', maxWidth: 1008 }}>
            <CreditsTable maxWidth={1008} />
          </Box>
        </FlexColCenHor>
        <FlexColCenHor
          sx={{
            width: '100%',
            pt: 0,
            mb: 0,
          }}
        >
          <FlexCenHor sx={{ width: '100%', mb: 7 }}>
            <Typography variant="body-lg-rg">{t(I18N_KEYS.PAYMENTS.PRICING_TABLE.FAQ_TITLE)}</Typography>
          </FlexCenHor>
          <FlexCenHor sx={{ width: '100%' }}>
            <PricingFAQS maxWidth={720} hasMaxTier={hasMaxCard} />
          </FlexCenHor>
        </FlexColCenHor>
      </FlexColCenHor>
      {container === PricingPageContainer.Page && (
        <FlexColCenHor data-testid="pricing-table-page-home-button" sx={{ width: '100%' }}>
          <AppIconButton
            aria-label={t(I18N_KEYS.GENERAL.ARIA.GO_TO_HOME)}
            onClick={() => navigate('/')}
            height={36}
            width={36}
            sx={{
              position: 'fixed',
              top: 17 + bannerOffset,
              left: 16,
              zIndex: 1,
            }}
          >
            <WeavyLogoFilled title="figma_weave_logo" width={24} height={24} style={{ color: fplColor.text }} />
          </AppIconButton>
        </FlexColCenHor>
      )}
    </>
  );
};
